// Simple touch-drag helper that mimics a subset of HTML5 drag/drop so touch
// users can drag palette/script blocks onto drop targets.
const TOUCH_DROP_EVENT = "touchdragdrop";

let activeDrag = null;

const defaultDropEffect = "copy";

const createDataTransfer = (store, dropEffect) => ({
    getData: (type) => (type in store ? store[type] : ""),
    setData: (type, value) => {
        store[type] = value;
    },
    clearData: () => {
        Object.keys(store).forEach((key) => delete store[key]);
    },
    dropEffect,
    effectAllowed: dropEffect === "move" ? "move" : "copy",
});

const getTouchById = (touchList, id) => {
    if (!touchList) return null;
    for (let i = 0; i < touchList.length; i += 1) {
        const touch = touchList[i];
        if (typeof id === "number") {
            if (touch.identifier === id) return touch;
        } else {
            return touch;
        }
    }
    return null;
};

const positionGhost = (ghost, x, y) => {
    if (!ghost) return;
    ghost.style.transform = `translate(${x - ghost.__offsetX}px, ${y - ghost.__offsetY}px) scale(0.98)`;
};

const createGhostElement = (sourceEl) => {
    if (typeof document === "undefined") return null;
    const ghost = sourceEl ? sourceEl.cloneNode(true) : document.createElement("div");
    const rect = sourceEl?.getBoundingClientRect();
    ghost.style.position = "fixed";
    ghost.style.pointerEvents = "none";
    ghost.style.opacity = "0.85";
    ghost.style.zIndex = "9999";
    ghost.style.left = "0px";
    ghost.style.top = "0px";
    ghost.style.width = rect ? `${rect.width}px` : "80px";
    ghost.style.height = rect ? `${rect.height}px` : "40px";
    ghost.__offsetX = rect ? rect.width / 2 : 40;
    ghost.__offsetY = rect ? rect.height / 2 : 20;
    ghost.classList?.add?.("touch-drag-ghost");
    return ghost;
};

const cleanupActiveDrag = () => {
    if (!activeDrag) return;
    const state = activeDrag;
    activeDrag = null;
    state.moveListener && window.removeEventListener("touchmove", state.moveListener);
    state.endListener && window.removeEventListener("touchend", state.endListener);
    state.cancelListener && window.removeEventListener("touchcancel", state.cancelListener);
    if (state.ghost && state.ghost.parentNode) {
        state.ghost.parentNode.removeChild(state.ghost);
    }
};

const dispatchDropEvent = (state, touch) => {
    if (typeof document === "undefined") return;
    const targetEl = document.elementFromPoint(touch.clientX, touch.clientY);
    const dropTarget = targetEl?.closest?.("[data-touch-drop-target]");
    if (!dropTarget) return;
    const customEvent = new CustomEvent(TOUCH_DROP_EVENT, {
        bubbles: true,
        cancelable: true,
        detail: {
            clientX: touch.clientX,
            clientY: touch.clientY,
            dataTransfer: state.dataTransfer,
        },
    });
    dropTarget.dispatchEvent(customEvent);
};

export const startTouchDrag = (touchEvent, options = {}) => {
    if (typeof window === "undefined") return false;
    if (!touchEvent?.touches?.length) return false;

    const firstTouch = touchEvent.touches[0];
    const payload = { ...(options.dataTransferData || {}) };
    const dropEffect = options.dropEffect || defaultDropEffect;
    const dataTransfer = createDataTransfer(payload, dropEffect);
    const ghost = createGhostElement(options.previewElement || touchEvent.currentTarget);
    if (ghost && document.body) {
        document.body.appendChild(ghost);
        positionGhost(ghost, firstTouch.clientX, firstTouch.clientY);
    }

    const state = {
        pointerId: firstTouch.identifier,
        dataTransfer,
        ghost,
        dropEffect,
        onEnd: typeof options.onEnd === "function" ? options.onEnd : null,
        onMove: typeof options.onMove === "function" ? options.onMove : null,
    };

    const finishDrag = (touch) => {
        state.lastPosition = { clientX: touch.clientX, clientY: touch.clientY };
        dispatchDropEvent(state, touch);
        cleanupActiveDrag();
        state.onEnd && state.onEnd({ clientX: touch.clientX, clientY: touch.clientY });
    };

    state.moveListener = (moveEvent) => {
        const touch = getTouchById(moveEvent.touches, state.pointerId) || moveEvent.touches[0];
        if (!touch) return;
        moveEvent.preventDefault();
        state.lastPosition = { clientX: touch.clientX, clientY: touch.clientY };
        positionGhost(state.ghost, touch.clientX, touch.clientY);
        state.onMove && state.onMove({ clientX: touch.clientX, clientY: touch.clientY });
    };

    state.endListener = (endEvent) => {
        const touch = getTouchById(endEvent.changedTouches, state.pointerId) || endEvent.changedTouches[0];
        if (!touch) return;
        endEvent.preventDefault();
        finishDrag(touch);
    };

    state.cancelListener = (cancelEvent) => {
        const touch = getTouchById(cancelEvent.changedTouches, state.pointerId) || cancelEvent.changedTouches[0];
        if (touch) {
            state.lastPosition = { clientX: touch.clientX, clientY: touch.clientY };
        }
        cleanupActiveDrag();
        state.onEnd && state.onEnd({ canceled: true, clientX: state.lastPosition?.clientX, clientY: state.lastPosition?.clientY });
    };

    window.addEventListener("touchmove", state.moveListener, { passive: false });
    window.addEventListener("touchend", state.endListener, { passive: false });
    window.addEventListener("touchcancel", state.cancelListener, { passive: false });

    activeDrag = state;
    touchEvent.preventDefault();
    return true;
};

export { TOUCH_DROP_EVENT };
